import styled from '@emotion/styled'
import { useRouter } from 'next/router'
import Head from 'next/head'
import Image from 'next/image'
import { Themed } from 'theme-ui'
import { GoogleMap, useLoadScript, Marker } from '@react-google-maps/api'

import { Container, ContainerFlex, Error, Loading } from '../components/UIComponents'
import { hittaHitEnglishLocale, hittaHitSwedishLocale } from '../locales'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { ErrorFallback } from '../components/ErrorFallback'

const center = {
  lat: 56.047739767017035,
  lng: 12.694152456343287
}

const mapOptions = {
  disableDefaultUI: true,
  zoomControl: true
}

/**
 * Function to render te map.
 *
 * @returns {HTMLElement} - The HTML Markup.
 */
function RenderMap() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_GOOGLE_MAPS_API_KEY
  })

  /**
   * Function to open the map - works on mobile too where it opens the app.
   *
   */
  function markerClick() {
    window.open(
      `https://www.google.se/maps/dir//JC+Psykologmottagning+AB,+Kullagatan+8,+252+20+Helsingborg/@56.0475582,12.6240916,12z/data=!3m1!4b1!4m8!4m7!1m0!1m5!1m1!1s0x46523235651d58d9:0x500dd036fd3e70cc!2m2!1d12.694131!2d56.047578`,
      '_blank'
    )
  }

  if (!isLoaded) {
    return (
      <ContainerFlex className="map-container">
        <Loading />
      </ContainerFlex>
    )
  }

  if (loadError) {
    return (
      <ContainerFlex className="map-container">
        <Error />
      </ContainerFlex>
    )
  }

  return (
    <GoogleMap
      id="google-map-container"
      mapContainerClassName="map-container"
      zoom={15}
      center={center}
      options={mapOptions}
    >
      <Marker onClick={markerClick} clickable={true} position={center} />
    </GoogleMap>
  )
}

/**
 * Renders the Hitta component.
 *
 * @returns {HTMLElement} - The HTML Markup.
 */
const Hitta = () => {
  const router = useRouter()
  const { locale } = router
  const t = locale === 'en' ? hittaHitEnglishLocale : hittaHitSwedishLocale

  return (
    <>
      <Head>
        <title>Hitta Hit - JC Psykologmottagning AB</title>
        <meta
          name="Description"
          content="Mottagningen är belägen på Kullagatan 8, 252 20 Helsingborg i en ljus och tyst lokal som ger lugn och harmoni vid samtal."
        />
        <link rel="icon" href="/img/logo.svg" />
      </Head>
      <Container>
        <HittaStyles>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <RenderMap />
          </ErrorBoundary>
          <div className="hidden-large-screen center">
            <Themed.h4>{t.googleMapsParagraphOne}</Themed.h4>
            <a
              href="https://www.google.se/maps/dir//JC+Psykologmottagning+AB,+Kullagatan+8,+252+20+Helsingborg/@56.0475582,12.6240916,12z/data=!3m1!4b1!4m8!4m7!1m0!1m5!1m1!1s0x46523235651d58d9:0x500dd036fd3e70cc!2m2!1d12.694131!2d56.047578"
              target="_blank"
              rel="noreferrer"
            >
              <Image src="/img/maps.png" alt="Maps" width={128} height={128} />
            </a>
            <Themed.p>{t.googleMapsParagraphTwo}</Themed.p>
          </div>

          <Themed.div className="center">
            <Image src="/img/entre.jpg" alt="Entre" width={245} height={358} priority={true} />
            <Themed.p>{t.paragraphOne}</Themed.p>
          </Themed.div>
        </HittaStyles>
      </Container>
    </>
  )
}

const HittaStyles = styled.section`
  p a {
    color: ${({ theme }) => theme.colors.text};
  }

  p a:hover {
    text-decoration: none;
  }

  .map-container {
    width: 100%;
    height: 50vh;
    display: flex;
  }

  #google-map-container {
    display: flex;
    margin-top: 3rem;
  }

  .hidden-large-screen {
    margin-bottom: 1rem;
    display: flex;
    flex-direction: column;
  }

  @media only screen and (max-width: 648px) {
    .map-container {
      display: none;
    }

    #google-map-container {
      display: none;
    }
  }

  @media only screen and (min-width: 649px) {
    .hidden-large-screen {
      display: none;
    }
  }
`

export default Hitta
